import { useState } from 'react'
import { Alert } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { ApiError } from '@/lib/api'
import type { ConnectionDetail, ConnectionType } from './detailTypes'
import { useDetailAction } from './useConnectionDetail'

const titles: Record<string, string> = {
  rename: 'Rename connection',
  remove: 'Remove connection',
  rotate: 'Rotate key',
  import: 'Edit import-from date',
}
const fieldLabels: Record<string, string> = {
  remove: 'Type the display name',
  rename: 'Display name',
  import: 'Import history from',
}
function initialValue(action: string, row: ConnectionDetail) {
  if (action === 'rename') return row.display_name
  return action === 'import' ? (row.import_from ?? '') : ''
}
function errorText(error: Error) {
  if (!(error instanceof ApiError)) return error.message
  const body = error.body as { errors?: Record<string, string[]> }
  return body.errors
    ? Object.entries(body.errors)
        .map(([name, messages]) => `${name}: ${messages.join(', ')}`)
        .join('; ')
    : error.message
}
export function ConnectionDetailDialog({
  row,
  action,
  type,
  onClose,
}: {
  row: ConnectionDetail
  action: string
  type?: ConnectionType
  onClose: () => void
}) {
  const [value, setValue] = useState(initialValue(action, row))
  const [credentials, setCredentials] = useState<Record<string, string>>({})
  const mutation = useDetailAction(row.id)
  const removing = action === 'remove'
  const attributes = () => {
    if (removing) return { status: 'removed' }
    if (action === 'rename') return { display_name: value }
    if (action === 'import') return { import_from: value || null }
    return { credentials }
  }
  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{titles[action]}</DialogTitle>
          <DialogDescription>
            {removing
              ? `Type ${row.display_name} to confirm. Imported records remain and syncing stops.`
              : `Update ${row.display_name}.`}
          </DialogDescription>
        </DialogHeader>
        <form
          onSubmit={(event) => {
            event.preventDefault()
            if (removing && value !== row.display_name) return
            mutation.mutate({ action, attributes: attributes() }, { onSuccess: onClose })
          }}
          className="space-y-4"
        >
          {mutation.error && <Alert>{errorText(mutation.error)}</Alert>}
          {action === 'rotate' ? (
            <CredentialFields type={type} values={credentials} onChange={setCredentials} />
          ) : (
            <div className="space-y-2">
              <Label htmlFor="connection-detail-value">{fieldLabels[action]}</Label>
              <Input
                id="connection-detail-value"
                type={action === 'import' ? 'date' : 'text'}
                value={value}
                onChange={(event) => setValue(event.target.value)}
              />
              {action === 'import' && (
                <p className="text-caption text-muted-foreground">Leave blank for all history.</p>
              )}
            </div>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant={removing ? 'destructive' : 'default'}
              disabled={mutation.isPending || (removing && value !== row.display_name)}
            >
              {mutation.isPending ? 'Saving…' : titles[action]}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
function CredentialFields({
  type,
  values,
  onChange,
}: {
  type?: ConnectionType
  values: Record<string, string>
  onChange: (values: Record<string, string>) => void
}) {
  return (
    <div className="space-y-4">
      {type?.credential_fields.map((field) => {
        const key = field.key ?? field.name.toLowerCase().replaceAll(' ', '_')
        return (
          <div key={key} className="space-y-2">
            <Label htmlFor={`credential-${key}`}>{field.name}</Label>
            <Input
              id={`credential-${key}`}
              type={field.secret ? 'password' : 'text'}
              required={field.required}
              autoComplete="off"
              value={values[key] ?? ''}
              onChange={(event) => onChange({ ...values, [key]: event.target.value })}
            />
          </div>
        )
      })}
    </div>
  )
}
